import { useState } from 'react'
import { SITE_ORIGIN } from '@/lib/site'

/**
 * The share row under a post's hero — four buttons, matching live: X, Facebook,
 * LinkedIn, copy-link.
 *
 * The hrefs deliberately do NOT match live. There, X and Facebook are `href="#"`
 * and `href=""`, and LinkedIn points at `social-share-links-dfc4cc.webflow.io` —
 * the demo domain of the Webflow template the bar came from — so sharing a post
 * to LinkedIn links to someone else's site. Each builds its URL from the post's
 * own canonical path instead.
 *
 * Class names are the capture's, so the ported styling still applies.
 */
const ICONS = {
  x: 'M17.1761 4H19.9362L13.9061 10.7774L21 20H15.4456L11.0951 14.4066L6.11723 20H3.35544L9.80517 12.7508L3 4H8.69545L12.6279 9.11262L17.1761 4ZM16.2073 18.3754H17.7368L7.86441 5.53928H6.2232L16.2073 18.3754Z',
  facebook:
    'M21 12.055C21 7.05406 16.9706 3 12 3C7.02943 3 3 7.05406 3 12.055C3 16.5745 6.29115 20.3207 10.5938 21V14.6725H8.30859V12.055H10.5938V10.0576C10.5938 7.78321 11.9414 6.52735 14.0033 6.52735C14.9925 6.52735 16.0268 6.70508 16.0268 6.70508V8.93083H14.8676C13.7254 8.93083 13.3688 9.64313 13.3688 10.3741V12.055H15.9193L15.5115 14.6725H13.3688V21C17.6714 20.3207 21 16.5745 21 12.055Z',
  linkedin:
    'M18.3362 18.339H15.6707V14.1622C15.6707 13.1662 15.6505 11.8845 14.2817 11.8845C12.892 11.8845 12.6797 12.9683 12.6797 14.0887V18.339H10.0142V9.75H12.5747V10.9209H12.6092C12.967 10.2436 13.837 9.53103 15.1367 9.53103C17.8375 9.53103 18.337 11.3138 18.337 13.6304V18.339H18.3362ZM7.00373 8.57526C6.14473 8.57526 5.45373 7.87801 5.45373 7.02201C5.45373 6.16601 6.14548 5.46951 7.00373 5.46951C7.85973 5.46951 8.55598 6.16601 8.55598 7.02201C8.55598 7.87801 7.85898 8.57526 7.00373 8.57526ZM8.34023 18.339H5.66723V9.75H8.34023V18.339ZM19.6697 3H4.32923C3.59498 3 3.00098 3.5805 3.00098 4.29675V19.7033C3.00098 20.4202 3.59573 21 4.32923 21H19.6675C20.4002 21 21.001 20.4202 21.001 19.7033V4.29675C21.001 3.5805 20.4002 3 19.6675 3H19.6697Z',
} as const

/* Stroke, not fill, unlike the three brand marks. */
const COPY_ICON =
  'M9.99149 13.255C10.3154 13.6885 10.7287 14.0472 11.2032 14.3067C11.6778 14.5663 12.2026 14.7206 12.742 14.7593C13.2814 14.7979 13.8228 14.72 14.3295 14.5308C14.8362 14.3416 15.2963 14.0456 15.6786 13.6627L17.9414 11.3977C18.6283 10.6857 19.0085 9.73209 18.9999 8.74228C18.9913 7.75246 18.5946 6.80563 17.8954 6.1057C17.1962 5.40577 16.2503 5.00874 15.2615 5.00014C14.2727 4.99154 13.32 5.37205 12.6088 6.05971L11.3114 7.35079M13.0085 11.745C12.6846 11.3115 12.2713 10.9528 11.7968 10.6933C11.3222 10.4337 10.7974 10.2794 10.258 10.2407C9.7186 10.2021 9.1772 10.28 8.67052 10.4692C8.16384 10.6584 7.70374 10.9544 7.32142 11.3373L5.05864 13.6023C4.37167 14.3143 3.99155 15.2679 4.00014 16.2577C4.00874 17.2475 4.40536 18.1944 5.10458 18.8943C5.80381 19.5942 6.7497 19.9913 7.73852 19.9999C8.72733 20.0085 9.67997 19.628 10.3912 18.9403L11.681 17.6492'

type Target = { key: keyof typeof ICONS; label: string; href: (url: string) => string; cls: string }

const TARGETS: Target[] = [
  {
    key: 'x',
    label: 'Share this post on X',
    href: (u) => `https://x.com/intent/post?url=${encodeURIComponent(u)}`,
    cls: 'blog_share-link',
  },
  {
    key: 'facebook',
    label: 'Share this post on Facebook',
    href: (u) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(u)}`,
    cls: 'blog_share-link-facebook',
  },
  {
    key: 'linkedin',
    label: 'Share this post on LinkedIn',
    href: (u) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(u)}`,
    cls: 'blog_share-link',
  },
]

export function ShareBar({ path }: { path: string }) {
  const url = `${SITE_ORIGIN}${path}`
  const [copied, setCopied] = useState(false)

  async function copyUrl() {
    await navigator.clipboard.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="sharelinks">
      <div className="text-block-19">Share:</div>
      <div className="div-block-24">
        {TARGETS.map((t) => (
          <a
            key={t.key}
            href={t.href(url)}
            target="_blank"
            rel="noopener noreferrer"
            title={t.label}
            className={t.cls}
          >
            <div className="blog_social-icon">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                role="img"
                aria-label={t.label}
              >
                <path d={ICONS[t.key]} fill="currentColor" />
              </svg>
            </div>
          </a>
        ))}
        <button
          type="button"
          onClick={copyUrl}
          title={copied ? 'Link copied' : 'Copy link'}
          className="blog_share-link cursor-pointer border-0 bg-transparent p-0"
        >
          <div className="blog_social-icon">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              role="img"
              aria-label={copied ? 'Link copied' : 'Copy link'}
            >
              <path
                d={COPY_ICON}
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </button>
      </div>
    </div>
  )
}
